import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../../App";
import { 
  Heart, Gift, Users, MessageCircle, Eye,
  TrendingUp, ArrowRight 
} from "lucide-react";

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API}/analytics/admin/overview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAnalytics(response.data);
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  const stats = [
    { 
      label: "Total Weddings", 
      value: analytics?.total_weddings || 0, 
      icon: Heart,
      color: "text-pink-400",
      link: "/admin/weddings"
    },
    { 
      label: "Total Artifacts", 
      value: analytics?.total_artifacts || 0, 
      icon: Gift,
      color: "text-purple-400",
      link: "/admin/artifacts"
    },
    { 
      label: "Tree Submissions", 
      value: analytics?.total_submissions || 0, 
      icon: MessageCircle,
      color: "text-green-400",
      link: null
    },
    { 
      label: "Total Scans", 
      value: analytics?.total_scans || 0, 
      icon: Eye,
      color: "text-blue-400",
      link: null
    },
  ];

  const quickActions = [
    { label: "Create Wedding", path: "/admin/weddings", icon: Heart },
    { label: "Manage Artifacts", path: "/admin/artifacts", icon: Gift },
    { label: "Manage Users", path: "/admin/users", icon: Users },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-wedding-ivory">Admin Dashboard</h1>
        <p className="text-wedding-ivory/60 mt-1">Manage all weddings and artifacts</p>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {stat.link ? (
                  <Link to={stat.link} className="stat-card block hover:border-wedding-gold/30 transition-colors">
                    <stat.icon className={`w-6 h-6 ${stat.color} mb-3`} />
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                  </Link>
                ) : (
                  <div className="stat-card">
                    <stat.icon className={`w-6 h-6 ${stat.color} mb-3`} />
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="font-serif text-xl text-wedding-ivory mb-4">Quick Actions</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {quickActions.map((action, index) => (
                <motion.div
                  key={action.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                >
                  <Link
                    to={action.path}
                    className="card-hover p-6 flex items-center gap-4 group"
                  >
                    <div className="w-12 h-12 rounded-full bg-wedding-gold/10 flex items-center justify-center group-hover:bg-wedding-gold/20 transition-colors">
                      <action.icon className="w-6 h-6 text-wedding-gold" />
                    </div>
                    <div className="flex-1">
                      <p className="text-wedding-ivory font-medium">{action.label}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-wedding-ivory/30 group-hover:text-wedding-gold group-hover:translate-x-1 transition-all" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          {analytics?.recent_events?.length > 0 && (
            <div className="card-hover p-6">
              <h2 className="font-serif text-xl text-wedding-ivory mb-6">Recent Activity</h2>
              <div className="space-y-4">
                {analytics.recent_events.slice(0, 10).map((event, index) => (
                  <div 
                    key={event.event_id || index}
                    className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full ${
                        event.event_type === "page_view" ? "bg-blue-400" :
                        event.event_type === "artifact_scan" ? "bg-purple-400" :
                        event.event_type === "tree_submit" ? "bg-green-400" :
                        "bg-wedding-gold"
                      }`}></span>
                      <span className="text-wedding-ivory capitalize">
                        {event.event_type?.replace("_", " ")}
                      </span>
                    </div>
                    <span className="text-wedding-ivory/50 text-sm">
                      {new Date(event.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
